import React, { useState, useEffect } from "react";
import {
  Download,
  Search,
  RefreshCw,
  Users,
  FileSpreadsheet,
  Phone,
  User,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Filter,
  X,
} from "lucide-react";
import axios from "axios";
import { apiUrl } from "../../constants";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

const CustomerDataTable = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [dateFilter, setDateFilter] = useState("all");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const { isLoggedIn, token } = useSelector((store) => store.userSlice);

  const fetchCustomers = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await axios.get(`${apiUrl}/user/allusers`, {
        headers: {
          token,
        },
      });
      if (response.data.success) {
        const fetchedData = response.data.data || [];
        console.log(fetchedData, "cus");
        setCustomers(fetchedData);
      } else {
        setError(response.data.message || "Failed to load customer data");
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch customers"
      );
      console.error("Error fetching customers:", err);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  };
  const getDateRange = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (dateFilter) {
      case "today":
        const todayEnd = new Date(today);
        todayEnd.setHours(23, 59, 59, 999);
        return { start: today, end: todayEnd };

      case "yesterday":
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayEnd = new Date(yesterday);
        yesterdayEnd.setHours(23, 59, 59, 999);
        return { start: yesterday, end: yesterdayEnd };

      case "last7days":
        const last7 = new Date(today);
        last7.setDate(last7.getDate() - 7);
        const last7End = new Date(now);
        last7End.setHours(23, 59, 59, 999);
        return { start: last7, end: last7End };

      case "last30days":
        const last30 = new Date(today);
        last30.setDate(last30.getDate() - 30);
        const last30End = new Date(now);
        last30End.setHours(23, 59, 59, 999);
        return { start: last30, end: last30End };

      case "thisMonth":
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        monthStart.setHours(0, 0, 0, 0);
        const monthEnd = new Date(now);
        monthEnd.setHours(23, 59, 59, 999);
        return { start: monthStart, end: monthEnd };

      case "lastMonth":
        const lastMonthStart = new Date(
          now.getFullYear(),
          now.getMonth() - 1,
          1
        );
        lastMonthStart.setHours(0, 0, 0, 0);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
        lastMonthEnd.setHours(23, 59, 59, 999);
        return { start: lastMonthStart, end: lastMonthEnd };

      case "thisYear":
        const yearStart = new Date(now.getFullYear(), 0, 1);
        yearStart.setHours(0, 0, 0, 0);
        const yearEnd = new Date(now);
        yearEnd.setHours(23, 59, 59, 999);
        return { start: yearStart, end: yearEnd };

      case "lastYear":
        const lastYearStart = new Date(now.getFullYear() - 1, 0, 1);
        lastYearStart.setHours(0, 0, 0, 0);
        const lastYearEnd = new Date(
          now.getFullYear() - 1,
          11,
          31,
          23,
          59,
          59,
          999
        );
        return { start: lastYearStart, end: lastYearEnd };

      case "custom":
        if (customStartDate && customEndDate) {
          const start = new Date(customStartDate);
          start.setHours(0, 0, 0, 0);
          const end = new Date(customEndDate);
          end.setHours(23, 59, 59, 999);
          return { start, end };
        }
        return null;

      default:
        return null;
    }
  };

  const filterByDate = (customer) => {
    if (dateFilter === "all") return true;

    const dateRange = getDateRange();
    if (!dateRange) return true;

    // Parse the ISO date string
    const regDate = new Date(customer.reg_date);

    // Check if date is valid
    if (isNaN(regDate.getTime())) return false;

    // Compare using timestamps for comparison including timezone
    return (
      regDate.getTime() >= dateRange.start.getTime() &&
      regDate.getTime() <= dateRange.end.getTime()
    );
  };

  const filteredCustomers = customers.filter((customer) => {
    const searchLower = searchTerm.toLowerCase();

    const nameMatch = customer.name?.toLowerCase().includes(searchLower);

    const phoneMatch = customer.phone?.includes(searchTerm);

    const dobMatch = formatDate(customer.dob)
      .toLowerCase()
      .includes(searchLower);

    const moneyMatch = customer.funingo_money?.toString().includes(searchTerm);

    const dateMatch = filterByDate(customer);

    return (nameMatch || phoneMatch || dobMatch || moneyMatch) && dateMatch;
  });

  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCustomers = filteredCustomers.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, dateFilter, customStartDate, customEndDate]);

  const downloadExcel = async () => {
    setDownloadingExcel(true);

    try {
      const response = await axios.get(`${apiUrl}/user/export-excel`, {
        responseType: "blob",
        headers: {
          token,
        },
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `FUNINGO_Customer_data_${new Date().toISOString().split("T")[0]}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Data Downloaded Successfully!");
    } catch (error) {
      console.error("Error downloading Excel file:", error);
      toast.error("Failed to download Excel file. Please try again.");
    } finally {
      setDownloadingExcel(false);
    }
  };

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const handleDateFilterChange = (value) => {
    setDateFilter(value);
    if (value === "custom") {
      setShowCustomDatePicker(true);
    } else {
      setShowCustomDatePicker(false);
      setCustomStartDate("");
      setCustomEndDate("");
    }
  };

  const clearFilters = () => {
    setDateFilter("all");
    setCustomStartDate("");
    setCustomEndDate("");
    setShowCustomDatePicker(false);
    setSearchTerm("");
  };

  const hasActiveFilters = dateFilter !== "all" || searchTerm !== "";

  return (
    <div
      style={{
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        padding: "40px 20px",
        minHeight: "100vh",
      }}
    >
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          position: "relative",
        }}
      >
        {downloadingExcel && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.7)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
              backdropFilter: "blur(4px)",
            }}
          >
            <div
              style={{
                background: "white",
                borderRadius: "20px",
                padding: "40px 60px",
                boxShadow: "0 25px 50px rgba(0, 0, 0, 0.5)",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  border: "6px solid #f3f4f6",
                  borderTop: "6px solid #10b981",
                  borderRadius: "50%",
                  margin: "0 auto 25px",
                  animation: "spin 1s linear infinite",
                }}
              />
              <h2
                style={{
                  margin: "0 0 10px 0",
                  fontSize: "24px",
                  fontWeight: "bold",
                  color: "#1a202c",
                }}
              >
                Downloading Excel...
              </h2>
              <p
                style={{
                  margin: 0,
                  color: "#6b7280",
                  fontSize: "16px",
                }}
              >
                Please wait while we prepare your file
              </p>
            </div>
          </div>
        )}

        {/* Header Card */}
        <div
          style={{
            background: "white",
            borderRadius: "20px",
            padding: "30px",
            marginBottom: "30px",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "20px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
              <div
                style={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  padding: "15px",
                  borderRadius: "15px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Users size={32} color="white" />
              </div>
              <div>
                <h1
                  style={{
                    margin: 0,
                    fontSize: "28px",
                    fontWeight: "bold",
                    color: "#1a202c",
                  }}
                >
                  Customer Database
                </h1>
                <p
                  style={{
                    margin: "5px 0 0 0",
                    color: "#718096",
                    fontSize: "14px",
                  }}
                >
                  {filteredCustomers.length} customers found
                </p>
              </div>
            </div>

            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <button
                onClick={fetchCustomers}
                disabled={loading}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "12px 24px",
                  background: "white",
                  border: "2px solid #667eea",
                  color: "#667eea",
                  borderRadius: "12px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: loading ? "not-allowed" : "pointer",
                  transition: "all 0.3s ease",
                  opacity: loading ? 0.6 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.target.style.background = "#667eea";
                    e.target.style.color = "white";
                    e.target.style.transform = "translateY(-2px)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "white";
                  e.target.style.color = "#667eea";
                  e.target.style.transform = "translateY(0)";
                }}
              >
                <RefreshCw
                  size={18}
                  style={{
                    animation: loading ? "spin 1s linear infinite" : "none",
                  }}
                />
                Refresh
              </button>

              <button
                onClick={downloadExcel}
                disabled={filteredCustomers.length === 0 || downloadingExcel}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "12px 24px",
                  background: downloadingExcel
                    ? "linear-gradient(135deg, #6b7280 0%, #4b5563 100%)"
                    : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                  border: "none",
                  color: "white",
                  borderRadius: "12px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor:
                    filteredCustomers.length === 0 || downloadingExcel
                      ? "not-allowed"
                      : "pointer",
                  transition: "all 0.3s ease",
                  boxShadow: downloadingExcel
                    ? "0 4px 12px rgba(107, 114, 128, 0.3)"
                    : "0 4px 12px rgba(16, 185, 129, 0.3)",
                  opacity:
                    filteredCustomers.length === 0 || downloadingExcel
                      ? 0.7
                      : 1,
                }}
                onMouseEnter={(e) => {
                  if (filteredCustomers.length > 0 && !downloadingExcel) {
                    e.target.style.transform = "translateY(-2px)";
                    e.target.style.boxShadow =
                      "0 6px 20px rgba(16, 185, 129, 0.4)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = downloadingExcel
                    ? "0 4px 12px rgba(107, 114, 128, 0.3)"
                    : "0 4px 12px rgba(16, 185, 129, 0.3)";
                }}
              >
                <Download
                  size={18}
                  style={{
                    animation: downloadingExcel
                      ? "spin 1s linear infinite"
                      : "none",
                  }}
                />
                {downloadingExcel
                  ? "Downloading..."
                  : `Download Excel (${filteredCustomers.length})`}
              </button>
            </div>
          </div>

          {/* Date Filter Section */}
          <div
            style={{
              marginTop: "25px",
              padding: "20px",
              background: "#f9fafb",
              borderRadius: "12px",
              border: "2px solid #e5e7eb",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                marginBottom: "15px",
              }}
            >
              <Calendar size={20} color="#667eea" />
              <span
                style={{
                  fontSize: "15px",
                  fontWeight: "600",
                  color: "#1f2937",
                }}
              >
                Filter by Registration Date
              </span>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "4px 12px",
                    background: "#ef4444",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    fontSize: "12px",
                    fontWeight: "600",
                    cursor: "pointer",
                    marginLeft: "auto",
                  }}
                >
                  <X size={14} />
                  Clear All
                </button>
              )}
            </div>

            <div
              style={{
                display: "flex",
                gap: "10px",
                flexWrap: "wrap",
              }}
            >
              {[
                { value: "all", label: "All Time" },
                { value: "today", label: "Today" },
                { value: "yesterday", label: "Yesterday" },
                { value: "last7days", label: "Last 7 Days" },
                { value: "last30days", label: "Last 30 Days" },
                { value: "thisMonth", label: "This Month" },
                { value: "lastMonth", label: "Last Month" },
                { value: "thisYear", label: "This Year" },
                { value: "lastYear", label: "Last Year" },
                { value: "custom", label: "Custom Range" },
              ].map((filter) => (
                <button
                  key={filter.value}
                  onClick={() => handleDateFilterChange(filter.value)}
                  style={{
                    padding: "8px 16px",
                    background:
                      dateFilter === filter.value
                        ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                        : "white",
                    color: dateFilter === filter.value ? "white" : "#1f2937",
                    border:
                      dateFilter === filter.value
                        ? "2px solid #667eea"
                        : "2px solid #e5e7eb",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (dateFilter !== filter.value) {
                      e.target.style.borderColor = "#667eea";
                      e.target.style.background = "#f3f4f6";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (dateFilter !== filter.value) {
                      e.target.style.borderColor = "#e5e7eb";
                      e.target.style.background = "white";
                    }
                  }}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            {/* Custom Date Range Picker */}
            {showCustomDatePicker && (
              <div
                style={{
                  marginTop: "15px",
                  padding: "15px",
                  background: "white",
                  borderRadius: "8px",
                  border: "2px solid #667eea",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: "15px",
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ flex: "1", minWidth: "200px" }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#6b7280",
                        marginBottom: "5px",
                      }}
                    >
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "10px",
                        border: "2px solid #e5e7eb",
                        borderRadius: "8px",
                        fontSize: "14px",
                        outline: "none",
                      }}
                    />
                  </div>
                  <div style={{ flex: "1", minWidth: "200px" }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#6b7280",
                        marginBottom: "5px",
                      }}
                    >
                      End Date
                    </label>
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "10px",
                        border: "2px solid #e5e7eb",
                        borderRadius: "8px",
                        fontSize: "14px",
                        outline: "none",
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Search Bar */}
          <div
            style={{
              marginTop: "20px",
              position: "relative",
            }}
          >
            <Search
              size={20}
              style={{
                position: "absolute",
                left: "15px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#9ca3af",
              }}
            />
            <input
              type="text"
              placeholder="Search by name, phone, date of birth, or Funingo Money..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                padding: "14px 14px 14px 45px",
                border: "2px solid #e5e7eb",
                borderRadius: "12px",
                fontSize: "15px",
                outline: "none",
                transition: "all 0.3s ease",
                boxSizing: "border-box",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#667eea";
                e.target.style.boxShadow = "0 0 0 3px rgba(102, 126, 234, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#e5e7eb";
                e.target.style.boxShadow = "none";
              }}
            />
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div
            style={{
              background: "#fee2e2",
              border: "2px solid #ef4444",
              borderRadius: "12px",
              padding: "16px",
              marginBottom: "20px",
              color: "#991b1b",
              fontSize: "14px",
            }}
          >
            ⚠️ {error}
          </div>
        )}

        {/* Table Card */}
        <div
          style={{
            background: "white",
            borderRadius: "20px",
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          }}
        >
          {loading ? (
            <div
              style={{
                padding: "60px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  width: "60px",
                  height: "60px",
                  border: "4px solid #f3f4f6",
                  borderTop: "4px solid #667eea",
                  borderRadius: "50%",
                  margin: "0 auto 20px",
                  animation: "spin 1s linear infinite",
                }}
              />
              <p style={{ color: "#6b7280", fontSize: "16px" }}>
                Loading customers...
              </p>
            </div>
          ) : filteredCustomers.length === 0 ? (
            <div
              style={{
                padding: "60px",
                textAlign: "center",
              }}
            >
              <FileSpreadsheet
                size={64}
                color="#d1d5db"
                style={{ margin: "0 auto 20px" }}
              />
              <p style={{ color: "#6b7280", fontSize: "18px", margin: 0 }}>
                No customers found
              </p>
              <p
                style={{ color: "#9ca3af", fontSize: "14px", marginTop: "8px" }}
              >
                {searchTerm || dateFilter !== "all"
                  ? "Try adjusting your filters"
                  : "Click refresh to load data"}
              </p>
            </div>
          ) : (
            <>
              {/* Items per page selector */}
              <div
                style={{
                  padding: "20px 24px",
                  borderBottom: "1px solid #f3f4f6",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "10px",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <span
                    style={{
                      color: "#6b7280",
                      fontSize: "14px",
                      fontWeight: "500",
                    }}
                  >
                    Show
                  </span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) =>
                      handleItemsPerPageChange(Number(e.target.value))
                    }
                    style={{
                      padding: "8px 12px",
                      border: "2px solid #e5e7eb",
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontWeight: "500",
                      cursor: "pointer",
                      outline: "none",
                      background: "white",
                    }}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span
                    style={{
                      color: "#6b7280",
                      fontSize: "14px",
                      fontWeight: "500",
                    }}
                  >
                    entries
                  </span>
                </div>
                <div
                  style={{
                    color: "#6b7280",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  Showing {startIndex + 1} to{" "}
                  {Math.min(endIndex, filteredCustomers.length)} of{" "}
                  {filteredCustomers.length} entries
                </div>
              </div>

              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        background:
                          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        color: "white",
                      }}
                    >
                      <th
                        style={{
                          padding: "18px 24px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "14px",
                          letterSpacing: "0.5px",
                          textTransform: "uppercase",
                        }}
                      >
                        S.No
                      </th>
                      <th
                        style={{
                          padding: "18px 24px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "14px",
                          letterSpacing: "0.5px",
                          textTransform: "uppercase",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <Phone size={16} />
                          Phone Number
                        </div>
                      </th>
                      <th
                        style={{
                          padding: "18px 24px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "14px",
                          letterSpacing: "0.5px",
                          textTransform: "uppercase",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <User size={16} />
                          Customer Name
                        </div>
                      </th>
                      <th
                        style={{
                          padding: "18px 24px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "14px",
                          letterSpacing: "0.5px",
                          textTransform: "uppercase",
                        }}
                      >
                        Funingo Money
                      </th>
                      <th
                        style={{
                          padding: "18px 24px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "14px",
                          letterSpacing: "0.5px",
                          textTransform: "uppercase",
                        }}
                      >
                        Date of Birth
                      </th>
                      <th
                        style={{
                          padding: "18px 24px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "14px",
                          letterSpacing: "0.5px",
                          textTransform: "uppercase",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <Calendar size={16} />
                          Registration Date
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentCustomers.map((customer, index) => (
                      <tr
                        key={startIndex + index}
                        style={{
                          borderBottom: "1px solid #f3f4f6",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#f9fafb";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "white";
                        }}
                      >
                        <td
                          style={{
                            padding: "18px 24px",
                            color: "#1f2937",
                            fontSize: "14px",
                            fontWeight: "500",
                          }}
                        >
                          {startIndex + index + 1}
                        </td>
                        <td
                          style={{
                            padding: "18px 24px",
                            color: "#1f2937",
                            fontSize: "14px",
                            fontWeight: "500",
                          }}
                        >
                          {customer.phone || "N/A"}
                        </td>
                        <td
                          style={{
                            padding: "18px 24px",
                            color: "#1f2937",
                            fontSize: "14px",
                            fontWeight: "600",
                          }}
                        >
                          {customer.name || "N/A"}
                        </td>
                        <td
                          style={{
                            padding: "18px 24px",
                            fontSize: "14px",
                          }}
                        >
                          <span
                            style={{
                              background:
                                "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                              color: "white",
                              padding: "6px 12px",
                              borderRadius: "8px",
                              fontWeight: "600",
                              display: "inline-block",
                            }}
                          >
                            ₹{customer.funingo_money || 0}
                          </span>
                        </td>
                        <td
                          style={{
                            padding: "18px 24px",
                            color: "#6b7280",
                            fontSize: "14px",
                          }}
                        >
                          {formatDate(customer.dob)}
                        </td>
                        <td
                          style={{
                            padding: "18px 24px",
                            color: "#6b7280",
                            fontSize: "14px",
                          }}
                        >
                          {formatDate(customer.reg_date)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div
                style={{
                  padding: "20px 24px",
                  borderTop: "1px solid #f3f4f6",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "15px",
                }}
              >
                <div
                  style={{
                    color: "#6b7280",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  Page {currentPage} of {totalPages}
                </div>

                <div
                  style={{ display: "flex", gap: "8px", alignItems: "center" }}
                >
                  <button
                    onClick={() => goToPage(1)}
                    disabled={currentPage === 1}
                    style={{
                      padding: "8px 12px",
                      background: currentPage === 1 ? "#f3f4f6" : "white",
                      border: "2px solid #e5e7eb",
                      borderRadius: "8px",
                      cursor: currentPage === 1 ? "not-allowed" : "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: currentPage === 1 ? "#9ca3af" : "#1f2937",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== 1) {
                        e.target.style.background = "#667eea";
                        e.target.style.color = "white";
                        e.target.style.borderColor = "#667eea";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== 1) {
                        e.target.style.background = "white";
                        e.target.style.color = "#1f2937";
                        e.target.style.borderColor = "#e5e7eb";
                      }
                    }}
                  >
                    First
                  </button>

                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{
                      padding: "8px 12px",
                      background: currentPage === 1 ? "#f3f4f6" : "white",
                      border: "2px solid #e5e7eb",
                      borderRadius: "8px",
                      cursor: currentPage === 1 ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== 1) {
                        e.target.style.background = "#667eea";
                        e.target.style.borderColor = "#667eea";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== 1) {
                        e.target.style.background = "white";
                        e.target.style.borderColor = "#e5e7eb";
                      }
                    }}
                  >
                    <ChevronLeft
                      size={18}
                      color={currentPage === 1 ? "#9ca3af" : "#1f2937"}
                    />
                  </button>

                  {/* Page Numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => goToPage(pageNum)}
                        style={{
                          padding: "8px 14px",
                          background:
                            currentPage === pageNum
                              ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                              : "white",
                          border:
                            currentPage === pageNum
                              ? "2px solid #667eea"
                              : "2px solid #e5e7eb",
                          color: currentPage === pageNum ? "white" : "#1f2937",
                          borderRadius: "8px",
                          cursor: "pointer",
                          fontSize: "14px",
                          fontWeight: "600",
                          minWidth: "40px",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          if (currentPage !== pageNum) {
                            e.target.style.background = "#f3f4f6";
                            e.target.style.borderColor = "#667eea";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (currentPage !== pageNum) {
                            e.target.style.background = "white";
                            e.target.style.borderColor = "#e5e7eb";
                          }
                        }}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: "8px 12px",
                      background:
                        currentPage === totalPages ? "#f3f4f6" : "white",
                      border: "2px solid #e5e7eb",
                      borderRadius: "8px",
                      cursor:
                        currentPage === totalPages ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== totalPages) {
                        e.target.style.background = "#667eea";
                        e.target.style.borderColor = "#667eea";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== totalPages) {
                        e.target.style.background = "white";
                        e.target.style.borderColor = "#e5e7eb";
                      }
                    }}
                  >
                    <ChevronRight
                      size={18}
                      color={currentPage === totalPages ? "#9ca3af" : "#1f2937"}
                    />
                  </button>

                  <button
                    onClick={() => goToPage(totalPages)}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: "8px 12px",
                      background:
                        currentPage === totalPages ? "#f3f4f6" : "white",
                      border: "2px solid #e5e7eb",
                      borderRadius: "8px",
                      cursor:
                        currentPage === totalPages ? "not-allowed" : "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: currentPage === totalPages ? "#9ca3af" : "#1f2937",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== totalPages) {
                        e.target.style.background = "#667eea";
                        e.target.style.color = "white";
                        e.target.style.borderColor = "#667eea";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== totalPages) {
                        e.target.style.background = "white";
                        e.target.style.color = "#1f2937";
                        e.target.style.borderColor = "#e5e7eb";
                      }
                    }}
                  >
                    Last
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default CustomerDataTable;
